import {
    StyledDeleteDialogHintText,
    StyledDialogHeader,
    StyledTargetDeletionNameText
} from '@/components/commons/delete-dialog/style'
import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Dialog, DialogBody, DialogFooter, DialogHeader, DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Text} from '@/components/ui/text/Text'
import {Trans, useTranslation} from '@/translations/i18n'
import {debounce} from '@/utilities/helpers'
import {DialogStore, useStoreState} from '@ariakit/react'
import {UseMutationResult} from '@tanstack/react-query'
import {ChangeEvent, ReactNode, useEffect, useState} from 'react'

interface DeleteDialogProps {
    canBeDeleted: boolean
    paragraph1: string
    deniedTitle: string
    deniedParagraph: ReactNode
    title: string
    targetNameToDelete: string
    // Generic over any mutation invoked with no arguments (`mutate()`); the dialog only reads
    // `isPending` and calls `mutate()`. Decoupled from any specific feature's payload type.
    deleteMutation: UseMutationResult<unknown, unknown, void, unknown>
    dialogStore: DialogStore
}

export const DeleteDialog = ({
    deleteMutation,
    title,
    paragraph1,
    canBeDeleted,
    targetNameToDelete,
    deniedTitle,
    deniedParagraph,
    dialogStore
}: DeleteDialogProps) => {
    const {t} = useTranslation()
    const [isConfirmationValid, setIsConfirmationValid] = useState(false)

    // The dialog content unmounts on hide (clearing the input), but this component stays mounted, so
    // reset the confirmation flag when the dialog closes — otherwise reopening shows an empty field
    // with the delete button still enabled.
    const isOpen = useStoreState(dialogStore, 'open')
    useEffect(() => {
        if (!isOpen) setIsConfirmationValid(false)
    }, [isOpen])

    const onInputChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value == 'Elimina') {
            setIsConfirmationValid(true)
        } else {
            setIsConfirmationValid(false)
        }
    }, 200)

    if (!canBeDeleted) {
        return (
            <>
                <Dialog store={dialogStore} width="400px">
                    <DialogHeader>
                        <DialogHeading>{deniedTitle}</DialogHeading>
                        <Text>{deniedParagraph}</Text>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => dialogStore.hide()}>
                            {t('commons:close')}
                        </Button>
                    </DialogFooter>
                </Dialog>
            </>
        )
    }

    return (
        <Dialog store={dialogStore} width="400px">
            <StyledDialogHeader>
                <DialogHeading>{title}</DialogHeading>
                <div>
                    <Text palette="neutral" shade="600" size="textSm">
                        {paragraph1}
                    </Text>
                    <StyledTargetDeletionNameText palette="neutral" shade="950" size="textSm" weight="500">
                        {targetNameToDelete}
                    </StyledTargetDeletionNameText>
                    <Text palette="neutral" shade="600" size="textSm">
                        {t('commons:delete_paragraph_2')}
                    </Text>
                </div>
            </StyledDialogHeader>
            <DialogBody gap={2}>
                <StyledDeleteDialogHintText size="textMd">
                    <Trans i18nKey="commons:delete_confirm_label" components={{b: <b />}} />
                </StyledDeleteDialogHintText>
                <InputText type="text" placeholder={t('commons:delete_confirm_placeholder')} onChange={onInputChange} />
            </DialogBody>
            <DialogFooter>
                <Button variant="secondary" onClick={() => dialogStore.hide()}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    variant="primaryDanger"
                    disabled={deleteMutation.isPending || !isConfirmationValid}
                    onClick={() => deleteMutation.mutate()}
                >
                    {t('commons:delete')}
                    {deleteMutation.isPending && <Spinner />}
                </Button>
            </DialogFooter>
        </Dialog>
    )
}
